import { DateTime } from "luxon"
import { DynamicConfigField, DynamicConfigKey, ExtendedDynamicConfigKey } from "./formTypes"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import {
    defaultPaymentMethod,
    isAccountingDimensionKey,
    isConfigLockedField,
    parseRRuleToCustomObject,
} from "./helpers"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { applyFieldFallback } from "./applyFieldFallback"
import { buildMultipleDeliveriesState } from "./buildMultipleDeliveriesState"
import { getFirstOrderNote } from "../drawer/deliveryNote/helpers"
import { getResponsibleSeed } from "../../../../../modules/order/pages/helpers"
import { defaultMultipleDeliveries } from "../../defaults"

// Only written when the order actually carries the time: yup rejects an explicit null on these keys,
// while an absent key stays valid for an order that has no meeting time yet. Returns whether anything
// was seeded, so a bare order can still fall through to the config fallback.
function seedMeetingTimePair(
    acc: Partial<Record<ExtendedDynamicConfigKey, any>>,
    orderDetails: MeetingServiceOrder,
    dateTimeFactory: DateTimeFactory,
): boolean {
    const { startDateTime, endDateTime } = orderDetails.meetingDetails ?? {}

    if (startDateTime) {
        acc[DynamicConfigKey.MEETING_START_TIME] = dateTimeFactory.fromApi(startDateTime)
    }
    if (endDateTime) {
        acc[DynamicConfigKey.MEETING_END_TIME] = dateTimeFactory.fromApi(endDateTime)
    }

    return Boolean(startDateTime || endDateTime)
}

export function mapConfigFieldsToValues({
    fields,
    user,
    dialogType,
    orderDetails,
    dateTimeFactory,
    flowConfig,
    mode,
}: {
    fields: DynamicConfigField[]
    user: SelectedMainUserState
    dialogType: CreateOrderDialogType
    orderDetails?: MeetingServiceOrder
    costCodeMap?: Map<number, { name?: string; value: string; label?: string; dimensionId: number }>
    accountingDimensionKeys?: { key: string; dimensionId?: number }[]
    dateTimeFactory: DateTimeFactory
    flowConfig?: CreateOrderDialogFlow
    mode?: CreateOrderDialogMode
}) {
    const accountingDimensionKeys = fields
        .filter((field) => isAccountingDimensionKey(field.key))
        .map((field) => ({
            key: field.key,
            dimensionId: Number(field.key.split("_")[2]),
        }))

    const costCodeMap = new Map<number, { name?: string; value: string; label?: string; dimensionId: number }>()
    orderDetails?.accountingDetails?.costCodes?.forEach((c) => {
        if (c.dimensionId && Number.isFinite(c.dimensionId)) {
            costCodeMap.set(c.dimensionId, c)
        }
    })
    return fields.reduce<Partial<Record<ExtendedDynamicConfigKey, any>>>((acc, field) => {
        const { key, inputField, selectValues } = field

        // Hidden fields with a predefined value are owned by the config: seed them before the
        // per-key mapping below, so a value saved on the order cannot override them.
        if (inputField && isConfigLockedField(field)) {
            applyFieldFallback(acc, inputField, key, selectValues)
            return acc
        }

        if (orderDetails) {
            const resolvedDeliveryLocation =
                orderDetails?.deliveryDetails?.location ?? orderDetails?.meetingDetails?.locationAsObject

            switch (key) {
                case DynamicConfigKey.RECURRENCY:
                    acc[key] = orderDetails.meetingDetails.recurrence
                        ? parseRRuleToCustomObject(orderDetails.meetingDetails.recurrence.pattern, dateTimeFactory)
                        : null
                    return acc

                case DynamicConfigKey.MEETING_NAME:
                    acc[key] = orderDetails.meetingDetails.meetingName || ""
                    return acc

                case DynamicConfigKey.MEETING_TIME:
                    acc[key] = orderDetails.meetingDetails.startDateTime
                        ? DateTime.fromSQL(orderDetails.meetingDetails.startDateTime)
                        : dateTimeFactory.now()
                    // A legacy config carries no start/end keys, but the drawer renders the saved
                    // meeting as that pair, so it is seeded from the order here as well.
                    seedMeetingTimePair(acc, orderDetails, dateTimeFactory)
                    return acc

                case DynamicConfigKey.MEETING_START_TIME:
                case DynamicConfigKey.MEETING_END_TIME:
                    if (seedMeetingTimePair(acc, orderDetails, dateTimeFactory)) return acc
                    break

                case DynamicConfigKey.DELIVERY_TYPE:
                    const prefilled = (selectValues && selectValues.find((o) => o.isPrefilled)?.value) || ""
                    acc[key] = orderDetails.deliveryDetails?.type || prefilled
                    return acc

                case DynamicConfigKey.DELIVERY_LOCATIONS:
                    acc[key] = resolvedDeliveryLocation?.id ? resolvedDeliveryLocation.id.toString() : ""
                    return acc

                case DynamicConfigKey.OTHER_DELIVERY_LOCATION:
                    acc[key] =
                        !resolvedDeliveryLocation?.id && resolvedDeliveryLocation?.name
                            ? resolvedDeliveryLocation.name
                            : ""
                    return acc

                case DynamicConfigKey.NUMBER_OF_SEATS:
                case DynamicConfigKey.NUMBER_OF_COVERS:
                    acc[key] = orderDetails.deliveryDetails?.sittings ?? undefined
                    return acc

                case DynamicConfigKey.BUYER_PARTIES:
                    acc[key] = orderDetails.buyerCustomerParty?.name
                        ? {
                              ...orderDetails.buyerCustomerParty,
                              id: orderDetails.buyerCustomerParty?.id,
                          }
                        : null
                    return acc

                case DynamicConfigKey.MERCHANT_ORDER_NOTE:
                    acc[key] = getFirstOrderNote(orderDetails?.deliveries)
                    return acc

                case DynamicConfigKey.PAYMENT_NOTE:
                    acc[key] = orderDetails.orderNote || ""
                    return acc

                case DynamicConfigKey.MULTIPLE_DELIVERIES: {
                    const md = buildMultipleDeliveriesState({
                        mode,
                        dialogType,
                        flowConfig,
                        orderDetails,
                        dateTimeFactory,
                    })
                    acc[key] = md ?? defaultMultipleDeliveries
                    return acc
                }

                case DynamicConfigKey.REGISTER_PARTICIPANTS:
                    acc[key] = (orderDetails.meetingDetails.attendees ?? []).map((a) => ({
                        name: a.name,
                        company: a.company,
                        isExternal: a.isExternal,
                    }))
                    return acc

                case DynamicConfigKey.MEETING_RESPONSIBLE:
                    const person = getResponsibleSeed(orderDetails.organizers)
                    acc[key] = person
                        ? { id: person.id, name: person.name, email: person.email }
                        : { name: user.displayName, id: user.userId }
                    return acc

                case DynamicConfigKey.PRIVATE_MEETING:
                    acc[key] = orderDetails.meetingDetails.isPrivate ?? true
                    return acc

                case DynamicConfigKey.EXTERNAL_MEETING:
                    acc[key] = orderDetails.meetingDetails.isExternal ?? false
                    return acc

                case DynamicConfigKey.PAYMENT_METHOD:
                    acc[key] = orderDetails?.paymentDetails?.method
                        ? { value: orderDetails?.paymentDetails?.method }
                        : defaultPaymentMethod
                    return acc

                case DynamicConfigKey.SATELLITES:
                    acc[key] = orderDetails?.deliveryDetails?.satellite ?? null
                    return acc

                // default:
                //     break
            }
        }

        /* ---------- Accounting Dimension (no orderDetails) ----------- */
        const dimMatch = key.match(/^ACCOUNTING_DIMENSION_\d+$/)
        if (dimMatch) {
            const dimId = accountingDimensionKeys.find((d) => d.key === key)?.dimensionId
            const costCode = dimId !== undefined ? costCodeMap.get(dimId) : null
            if (!costCode) {
                acc[key] = ""
                return acc
            }
            // Only the autocomplete renderer consumes the option object; scalar renderers
            // (FormikTextField etc.) bind the value directly, so they must get the raw string.
            acc[key] = inputField?.type === "autocomplete" ? { ...costCode, id: costCode.value } : costCode.value
            return acc
        }

        /* ---------- Default behavior (when orderDetails is not available or no mapping exists) ------------------------------- */
        if (key === DynamicConfigKey.MEETING_RESPONSIBLE) {
            acc[key] = { name: user.displayName, id: user.userId }
            return acc
        }

        if (key === DynamicConfigKey.MULTIPLE_DELIVERIES) {
            acc[key] = defaultMultipleDeliveries
            return acc
        }

        if (key === DynamicConfigKey.DELIVERY_LOCATIONS) {
            acc[key] = ""
            return acc
        }

        if (key === DynamicConfigKey.PAYMENT_METHOD) {
            acc[key] = defaultPaymentMethod
            return acc
        }

        /* ---------- Fallback -------------------------- */
        if (inputField) {
            applyFieldFallback(acc, inputField, key, selectValues)
        }

        return acc
    }, {})
}
